import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formValues, setFormValues] = useState({ name: '', email: '', message: '' });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setFormValues({ name: '', email: '', message: '' });
      return;
    }

    if (formValues.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9omtjgsenbnioleshu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormValues({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后再试' : serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <section className="mb-16">
            <div className="text-center mb-10">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4">
                关于汗汗漫画
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
                汗汗漫画网是一个专注于提供优质漫画阅读体验的在线平台，汇聚韩漫、日漫、国漫等主流漫画类型，致力于为漫画爱好者打造流畅、舒适的在线看漫画环境。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  成为全球漫画爱好者首选的在线阅读平台，让每个人都能便捷地享受到优质漫画内容，推动漫画文化的传播与发展。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  打造免费、便捷、流畅的漫画阅读平台，让漫画爱好者随时随地畅享韩漫、日漫、国漫等丰富漫画内容。
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-shield-star-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">核心价值</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  坚持免费提供漫画全集，采用创新的下拉式连续阅读模式，为用户创造极致的在线看漫画体验。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16 py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  从零开始，一步步打造最好的漫画阅读平台
                </p>
              </div>

              <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading text-sm">
                    2019
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-foreground-900 mb-1">梦想起航</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      汗汗漫画网正式上线，以"免费漫画全集"为初心，开始收录第一批韩漫和日漫作品。创始团队怀揣着对漫画的热爱，希望打造一个真正服务于漫画爱好者的免费平台。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-heading text-sm">
                    2021
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-foreground-900 mb-1">技术革新</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      引入下拉式连续阅读技术，彻底改变传统漫画翻页阅读模式。用户可以像刷短视频一样流畅地阅读漫画，大幅提升了阅读体验。同时开始大规模扩充国漫内容，实现三大主流漫画类型全覆盖。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600 font-heading text-sm">
                    2023
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-foreground-900 mb-1">快速成长</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      用户突破百万，漫画分类扩展至10+大类，日活读者持续增长。完成了多端适配优化，无论是手机、平板还是电脑，都能获得一致的流畅阅读体验。漫画库总量突破3000部。
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading text-sm">
                    2026
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-foreground-900 mb-1">持续进化</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      汗汗漫画网已成为国内领先的免费漫画阅读平台之一，拥有数百万活跃用户。我们持续优化推荐算法和阅读体验，不断丰富漫画内容库，用技术创新为漫画爱好者创造更优质的阅读服务。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                来自数百万读者的真实评价，是我们不断前进的动力
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <i
                        key={starIdx}
                        className={`ri-star-fill text-sm ${
                          starIdx < t.rating ? 'text-primary-500' : 'text-foreground-200'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.comment}&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-medium">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.date}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16 py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">
                  有任何问题或建议？欢迎随时联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">contact@hanhancomic.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-customer-service-2-line"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900 mb-1">在线客服</h4>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-question-answer-line"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-900 mb-1">常见问题</h4>
                      <p className="text-sm text-foreground-500">
                        建议先查看
                        <a href="/" className="text-primary-600 hover:text-primary-700 mx-1">首页常见问题</a>
                        板块
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <form
                    onSubmit={handleFormSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        value={formValues.name}
                        onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        value={formValues.email}
                        onChange={(e) => setFormValues((v) => ({ ...v, email: e.target.value }))}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言（最多500字）"
                        value={formValues.message}
                        onChange={(e) => setFormValues((v) => ({ ...v, message: e.target.value }))}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50 resize-none"
                      ></textarea>
                      <div className="text-xs text-foreground-400 mt-1 text-right">
                        {formValues.message.length}/500
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">留言发送成功！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于汗汗漫画',
            description: '汗汗漫画网是一个专注于提供优质漫画阅读体验的在线平台，汇聚韩漫、日漫、国漫等主流漫画类型。',
            about: {
              '@type': 'Organization',
              name: '汗汗漫画网',
              url: typeof window !== 'undefined' ? window.location.origin : '',
              description: '免费在线漫画阅读平台 - 韩漫日漫国漫免费漫画全集',
              contactPoint: {
                '@type': 'ContactPoint',
                email: 'contact@hanhancomic.com',
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
            },
          }),
        }}
      />
    </>
  );
}